import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  extractDeepLinkHrefFromNotification,
  parseDeepLink,
} from './parseDeepLink';

export const PENDING_DEEP_LINK_KEY = 'pendingDeepLinkHref';
export const PENDING_DEEP_LINK_TTL_MS = 24 * 60 * 60 * 1000;

type QueuedDeepLink = {
  href: string;
  receivedAt: number;
};

let inMemoryPending: QueuedDeepLink | null = null;
let persistPromise: Promise<void> | null = null;

const isQueuedDeepLink = (value: unknown): value is QueuedDeepLink => {
  if (!value || typeof value !== 'object') return false;
  const payload = value as Record<string, unknown>;
  return (
    typeof payload.href === 'string' &&
    payload.href.length > 0 &&
    typeof payload.receivedAt === 'number'
  );
};

const isFreshQueuedDeepLink = (value: QueuedDeepLink): boolean =>
  Date.now() - value.receivedAt <= PENDING_DEEP_LINK_TTL_MS &&
  !!parseDeepLink(value.href);

export const queueDeepLinkHref = async (href: string): Promise<boolean> => {
  if (!parseDeepLink(href)) return false;
  const queued: QueuedDeepLink = { href, receivedAt: Date.now() };
  inMemoryPending = queued;
  const write = AsyncStorage.setItem(
    PENDING_DEEP_LINK_KEY,
    JSON.stringify(queued),
  ).then(() => undefined);
  persistPromise = write.catch(() => undefined);
  await write;
  return true;
};

export const queueDeepLinkFromNotification = async (
  data: unknown,
): Promise<boolean> => {
  const href = extractDeepLinkHrefFromNotification(data);
  if (!href) return false;
  return queueDeepLinkHref(href);
};

export const consumeQueuedDeepLinkHref = async (): Promise<string | null> => {
  if (persistPromise) {
    await persistPromise;
    persistPromise = null;
  }

  const fromMemory = inMemoryPending;
  inMemoryPending = null;

  const raw = await AsyncStorage.getItem(PENDING_DEEP_LINK_KEY);
  if (raw) {
    await AsyncStorage.removeItem(PENDING_DEEP_LINK_KEY);
  }

  if (fromMemory && isFreshQueuedDeepLink(fromMemory)) {
    return fromMemory.href;
  }

  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isQueuedDeepLink(parsed) || !isFreshQueuedDeepLink(parsed)) {
      return null;
    }
    return parsed.href;
  } catch {
    return null;
  }
};
